import * as Sentry from "@sentry/react";

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isSupabaseLockStealAbortError(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.name === "AbortError" &&
    error.message.includes("steal")
  );
}

interface RetryOptions {
  retries?: number;
  initialDelayMs?: number;
  label?: string;
  metadata?: Record<string, string | number | boolean | null | undefined>;
}

function logAuthDiagnostic(
  message: string,
  options: {
    level?: "info" | "warning" | "error";
    metadata?: Record<string, string | number | boolean | null | undefined>;
    error?: unknown;
  } = {},
) {
  const { level = "info", metadata, error } = options;
  const payload = metadata ? { ...metadata } : undefined;

  Sentry.addBreadcrumb({
    category: "auth",
    message,
    level,
    data: payload,
  });

  if (level === "error") {
    if (error instanceof Error) {
      Sentry.captureException(error, {
        tags: {
          area: "auth",
        },
        extra: payload,
      });
    } else {
      Sentry.captureMessage(message, {
        level: "error",
        tags: {
          area: "auth",
        },
        extra: payload,
      });
    }
  } else if (level === "warning") {
    Sentry.captureMessage(message, {
      level: "warning",
      tags: {
        area: "auth",
      },
      extra: payload,
    });
  }

  const consoleMethod =
    level === "error"
      ? console.error
      : level === "warning"
        ? console.warn
        : console.info;

  if (error) {
    consoleMethod(message, payload, error);
    return;
  }

  consoleMethod(message, payload);
}

export function logAuthTransition(
  message: string,
  metadata?: Record<string, string | number | boolean | null | undefined>,
) {
  logAuthDiagnostic(message, {
    level: "info",
    metadata,
  });
}

export async function withSupabaseAuthRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> {
  const { retries = 2, initialDelayMs = 25, label, metadata } = options;

  let attempt = 0;

  for (;;) {
    try {
      const result = await operation();

      if (attempt > 0) {
        logAuthDiagnostic("Supabase auth operation succeeded after retry", {
          metadata: {
            label,
            attempts: attempt + 1,
            ...metadata,
          },
        });
      }

      return result;
    } catch (error) {
      if (!isSupabaseLockStealAbortError(error) || attempt >= retries) {
        logAuthDiagnostic("Supabase auth operation failed", {
          level: "error",
          metadata: {
            label,
            attempts: attempt + 1,
            is_lock_steal_abort: isSupabaseLockStealAbortError(error),
            ...metadata,
          },
          error,
        });
        throw error;
      }

      logAuthDiagnostic("Supabase auth lock conflict, retrying", {
        level: "warning",
        metadata: {
          label,
          attempt: attempt + 1,
          max_attempts: retries + 1,
          delay_ms: initialDelayMs * 2 ** attempt,
          ...metadata,
        },
        error,
      });

      await wait(initialDelayMs * 2 ** attempt);
      attempt += 1;
    }
  }
}
